// The incident's working record, shown in the incident panel: the running log merged with the
// milestones and notifications into one timeline, the post-incident review and the improvements
// it raises as corrective actions, plus the severity guide beside the severity picker. Every rule
// (log kinds, when a review is due, what "complete" means, the guide's wording) comes from the
// API: backend/src/lib/incidentLog.ts and lib/postIncidentReview.ts.

import { PolicyShortfallRaise } from "../../components/PolicyShortfalls";
import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { KIND_HREFS } from "../../components/EntityTags";
import { EditButton, RecordActions } from "../../components/RecordView";
import { StatusTag, type StatusTone } from "../../components/Drawing";
import { fmtDateTime, toLocalInput, fromInput, nowLocalInput, StatusPill, Field, input, textarea, secondaryBtn, ModalOverlay, ModalHeader } from "./shared";

// ── Types from GET /resilience/incident-insights and /incidents/:id/log ──────

export type ReviewState = "not_required" | "not_yet_due" | "due" | "overdue" | "incomplete" | "complete";

export interface ReviewStatus {
  state: ReviewState;
  required: boolean;
  reasons: string[];
  dueAt: string | null;
  missing: string[];
  attention: boolean;
}

export interface IncidentGuide {
  logKinds: Array<{ id: string; label: string; hint: string }>;
  severity: Array<{ severity: string; summary: string; examples: string }>;
  severityNote: string;
  reviewOverdueDays: number;
}

export interface IncidentInsights {
  reviews: Record<string, ReviewStatus>;
  containment: { meanHours: number | null; count: number; days: number };
  guide: IncidentGuide;
}

interface TimelineItem { id: string; at: string; source: "milestone" | "log" | "notification"; kind: string; title: string; text: string | null; author: string | null }
interface LogEntry { id: string; at: string; kind: string; text: string; authorName: string | null }
interface ReviewAction { id: string; displayId: number | null; title: string; status: string; dueDate: string | null }

interface LogView {
  entries: LogEntry[];
  timeline: TimelineItem[];
  review: {
    pirHeldAt: string | null; pirAttendees: string | null; pirWentWell: string | null; pirToImprove: string | null;
    status: ReviewStatus; improvements: string[]; actions: ReviewAction[];
  };
}

export function useIncidentInsights(refreshKey: unknown): IncidentInsights | null {
  const [data, setData] = useState<IncidentInsights | null>(null);
  useEffect(() => {
    let live = true;
    api.get<IncidentInsights>("/resilience/incident-insights").then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [refreshKey]);
  return data;
}

export const REVIEW_PILL: Record<ReviewState, { value: string; label: string; tone: StatusTone }> = {
  not_required: { value: "not_required", label: "Review optional", tone: "none" },
  not_yet_due:  { value: "planned", label: "Review after resolution", tone: "none" },
  due:          { value: "pending", label: "Review due", tone: "partial" },
  overdue:      { value: "overdue", label: "Review overdue", tone: "gap" },
  incomplete:   { value: "blocked", label: "Review incomplete", tone: "partial" },
  complete:     { value: "completed", label: "Reviewed", tone: "met" },
};

const h3: React.CSSProperties = { ...text.sectionTitle, margin: "0 0 10px" };

// ── Severity guide ───────────────────────────────────────────────────────────

/** The guide beside the severity picker; the selected level is highlighted. */
export function IncidentSeverityGuide({ guide, selected }: { guide: IncidentGuide | null; selected: string }) {
  const [open, setOpen] = useState(false);
  if (!guide) return null;
  const current = guide.severity.find((g) => g.severity === selected);
  return (
    <div style={{ ...text.hint, gridColumn: "1 / -1", lineHeight: 1.5, padding: "8px 10px", borderRadius: 3, background: theme.color.panelAlt }}>
      {current && <div><strong style={{ color: theme.color.text, textTransform: "capitalize" }}>{current.severity}:</strong> {current.summary}</div>}
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ background: "none", border: "none", padding: 0, marginTop: 4, cursor: "pointer", color: "var(--c-accentText)", fontSize: text.hint.fontSize, fontWeight: 600, fontFamily: theme.font }}>
        {open ? "Hide the severity guide" : "Show the severity guide"}
      </button>
      {open && (
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          {guide.severity.map((g) => (
            <div key={g.severity} style={{ padding: "6px 8px", borderRadius: 3, border: `1px solid ${g.severity === selected ? theme.color.accent : "var(--c-border)"}` }}>
              <div><strong style={{ color: theme.color.text, textTransform: "capitalize" }}>{g.severity}.</strong> {g.summary}</div>
              <div style={{ color: theme.color.subtle, marginTop: 2 }}>For example: {g.examples}</div>
            </div>
          ))}
          <div>{guide.severityNote}</div>
        </div>
      )}
    </div>
  );
}

// ── Log and timeline ─────────────────────────────────────────────────────────

const DOT: Record<TimelineItem["source"], string> = {
  milestone: theme.color.accent,
  notification: theme.color.success,
  log: theme.color.borderStrong,
};

/**
 * The log section and the review card share one load of GET /incidents/:id/log, so this renders
 * both; `onChanged` lets the register refresh its tiles after a review is saved.
 */
export function IncidentWorkingRecord({ incidentId, severity, canEdit, guide, onChanged }: {
  incidentId: string; severity: string; canEdit: boolean; guide: IncidentGuide | null; onChanged: () => void;
}) {
  const [view, setView] = useState<LogView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setView(await api.get<LogView>(`/resilience/incidents/${incidentId}/log`)); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the incident log")); }
  }, [incidentId]);
  useEffect(() => { void load(); }, [load]);

  if (!view) return error ? <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div> : null;
  return (
    <>
      <IncidentLog view={view} incidentId={incidentId} canEdit={canEdit} guide={guide} onSaved={(v) => setView(v)} />
      <IncidentReviewCard view={view} incidentId={incidentId} severity={severity} canEdit={canEdit} guide={guide} onSaved={(v) => { setView(v); onChanged(); }} onReload={load} />
    </>
  );
}

function IncidentLog({ view, incidentId, canEdit, guide, onSaved }: { view: LogView; incidentId: string; canEdit: boolean; guide: IncidentGuide | null; onSaved: (v: LogView) => void }) {
  const kinds = guide?.logKinds ?? [];
  const [kind, setKind] = useState("observation");
  const [at, setAt] = useState(nowLocalInput());
  const [note, setNote] = useState("");
  const [corrects, setCorrects] = useState<LogEntry | null>(null);
  // The entry form opens on purpose (Add a log entry, or Correct on an entry); reading the log never shows it.
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await api.post<LogView>(`/resilience/incidents/${incidentId}/log`, { kind, at: fromInput(at), text: note.trim(), correctsEntryId: corrects?.id ?? null });
      onSaved(res);
      setNote(""); setCorrects(null); setAt(nowLocalInput()); setAdding(false);
    } catch (err) { setError(apiErrorMessage(err, "Could not add the entry")); }
    finally { setSaving(false); }
  }

  const entryById = new Map(view.entries.map((e) => [e.id, e]));
  return (
    <section style={{ ...card, padding: 16 }}>
      <h3 style={h3}>Log</h3>
      <p style={{ ...text.hint, margin: "0 0 10px" }}>What was seen, done, decided, said and kept, in time order with the incident's own milestones and the notifications sent. Entries cannot be edited or deleted: to correct one, add a correction.</p>
      {canEdit && !adding && (
        <div style={{ marginBottom: 12 }}>
          <button type="button" style={secondaryBtn} onClick={() => { setCorrects(null); setAdding(true); }}>Add a log entry</button>
        </div>
      )}
      {canEdit && adding && (
        <form onSubmit={add} style={{ ...card, padding: 12, marginBottom: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, alignItems: "end" }}>
          <Field label="Kind">
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={input} title={kinds.find((k) => k.id === kind)?.hint}>
              {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="When"><input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} style={input} required /></Field>
          <Field label={corrects ? `Correction to the entry of ${fmtDateTime(corrects.at)}` : "Entry"}>
            <input value={note} onChange={(e) => setNote(e.target.value)} style={input} placeholder={kinds.find((k) => k.id === kind)?.hint ?? "What happened"} maxLength={5000} />
          </Field>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="submit" disabled={saving || !note.trim()} style={{ ...btnPrimary, opacity: saving || !note.trim() ? 0.6 : 1 }}>{saving ? "Adding…" : "Add"}</button>
            <button type="button" style={secondaryBtn} onClick={() => { setCorrects(null); setAdding(false); }}>Cancel</button>
          </div>
          {error && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        </form>
      )}
      {view.timeline.length === 0 ? <div style={{ ...text.body, color: theme.color.muted }}>Nothing recorded yet.</div> : (
        <ol style={{ listStyle: "none", margin: 0, padding: 0, borderLeft: `2px solid ${theme.color.border}`, marginLeft: 6 }}>
          {view.timeline.map((t) => {
            const entry = t.source === "log" ? entryById.get(t.id.slice(4)) : undefined;
            return (
              <li key={t.id} style={{ position: "relative", padding: "0 0 12px 16px" }}>
                <span style={{ position: "absolute", left: -6, top: 4, width: 10, height: 10, borderRadius: 999, background: DOT[t.source] }} />
                <div style={text.meta}>{fmtDateTime(t.at)}{t.author ? ` · ${t.author}` : ""}</div>
                <div style={{ ...text.body, lineHeight: 1.45 }}>
                  <strong style={{ fontWeight: t.source === "log" ? 600 : 700, color: t.source === "log" ? theme.color.muted : theme.color.heading }}>{t.title}</strong>
                  {t.text ? <span style={{ whiteSpace: "pre-wrap" }}>{t.source === "log" ? ": " : " · "}{t.text}</span> : null}
                  {entry && canEdit && (
                    <button type="button" onClick={() => { setCorrects(entry); setKind(entry.kind); setAdding(true); }} style={{ marginLeft: 8, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--c-accentText)", fontSize: text.meta.fontSize, fontFamily: theme.font }}>Correct</button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// ── Post-incident review ─────────────────────────────────────────────────────

function IncidentReviewCard({ view, incidentId, severity, canEdit, guide, onSaved, onReload }: {
  view: LogView; incidentId: string; severity: string; canEdit: boolean; guide: IncidentGuide | null;
  onSaved: (v: LogView) => void; onReload: () => Promise<void>;
}) {
  const r = view.review;
  const [editing, setEditing] = useState(false);
  const [heldAt, setHeldAt] = useState(toLocalInput(r.pirHeldAt) || nowLocalInput());
  const [attendees, setAttendees] = useState(r.pirAttendees ?? "");
  const [wentWell, setWentWell] = useState(r.pirWentWell ?? "");
  const [toImprove, setToImprove] = useState(r.pirToImprove ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHeldAt(toLocalInput(r.pirHeldAt) || nowLocalInput()); setAttendees(r.pirAttendees ?? ""); setWentWell(r.pirWentWell ?? ""); setToImprove(r.pirToImprove ?? "");
  }, [r.pirHeldAt, r.pirAttendees, r.pirWentWell, r.pirToImprove]);

  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy("save"); setError(null);
    try {
      onSaved(await api.put<LogView>(`/resilience/incidents/${incidentId}/review`, { pirHeldAt: fromInput(heldAt), pirAttendees: attendees, pirWentWell: wentWell, pirToImprove: toImprove }));
      setEditing(false);
    } catch (err) { setError(apiErrorMessage(err, "Could not save the review")); }
    finally { setBusy(null); }
  }

  const raised = new Set(r.actions.map((a) => a.title.trim().toLowerCase()));
  const pending = r.improvements.filter((t) => !raised.has(t.trim().toLowerCase()));
  const priority = severity === "critical" || severity === "high" ? "high" : "medium";

  async function raise(titles: string[]) {
    setBusy("raise"); setError(null);
    try {
      for (const title of titles) {
        await api.post("/improvement/actions", {
          title, source: "incident", incidentId, sourceRef: `incident-review:${incidentId}`, priority,
          description: "Raised from the post-incident review (ISO 27001 A.5.27, NCSC CAF D2.b).",
        });
      }
      await onReload();
    } catch (err) { setError(apiErrorMessage(err, "Could not raise the corrective action")); }
    finally { setBusy(null); }
  }

  const pill = REVIEW_PILL[r.status.state];
  return (
    <section style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ ...h3, margin: 0 }}>Post-incident review</h3>
        <StatusTag tone={pill.tone}>{pill.label}</StatusTag>
        <span style={{ flex: 1 }} />
        {canEdit && <EditButton onClick={() => setEditing(true)} label={r.pirHeldAt ? "Edit review" : "Record the review"} />}
      </div>
      <div style={{ ...text.hint, lineHeight: 1.5, marginBottom: 10 }}>
        {r.status.required
          ? <>Required: {r.status.reasons.join("; ").toLowerCase()}. {r.status.dueAt ? `Hold it by ${fmtDateTime(r.status.dueAt)} (${guide?.reviewOverdueDays ?? 14} days after resolution).` : "Due once the incident is resolved."}</>
          : "Not required for this incident, though one can always be recorded."}
        {r.status.missing.length > 0 && r.pirHeldAt ? ` Still missing: ${r.status.missing.join(", ")}.` : ""}
      </div>

      {editing && (
        <ModalOverlay onClose={() => setEditing(false)}>
        <ModalHeader title="Post-incident review" onClose={() => setEditing(false)} />
        <form onSubmit={save} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Held on"><input type="datetime-local" value={heldAt} onChange={(e) => setHeldAt(e.target.value)} style={input} required /></Field>
          <Field label="Who attended"><input value={attendees} onChange={(e) => setAttendees(e.target.value)} style={input} placeholder="Names and roles" /></Field>
          <Field label="What went well" span2><textarea rows={3} value={wentWell} onChange={(e) => setWentWell(e.target.value)} style={textarea} /></Field>
          <Field label="What should change" span2 help="One improvement per line: each line can then be raised as its own corrective action."><textarea rows={4} value={toImprove} onChange={(e) => setToImprove(e.target.value)} style={textarea} /></Field>
          {error && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
            <button type="submit" disabled={busy === "save"} style={{ ...btnPrimary, opacity: busy === "save" ? 0.6 : 1 }}>{busy === "save" ? "Saving…" : "Save review"}</button>
            <button type="button" style={secondaryBtn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
        </ModalOverlay>
      )}
      {r.pirHeldAt ? (
        <div style={{ ...text.body, display: "grid", gap: 10 }}>
          <div style={{ color: theme.color.muted }}>Held {fmtDateTime(r.pirHeldAt)}{r.pirAttendees ? ` with ${r.pirAttendees}` : ""}.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
            {([["What went well", r.pirWentWell], ["What should change", r.pirToImprove]] as const).map(([label, value]) => (
              <div key={label}>
                <div style={{ ...text.label, marginBottom: 4 }}>{label}</div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, color: value ? theme.color.text : theme.color.faint }}>{value || "Not recorded."}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {r.pirHeldAt && <PolicyShortfallRaise sourceKind="incident_review" sourceId={incidentId} canRaise={canEdit} />}

      {(r.improvements.length > 0 || r.actions.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ ...text.label, marginBottom: 6 }}>Improvements as corrective actions</div>
          <div style={{ display: "grid", gap: 6 }}>
            {r.actions.map((a) => (
              <div key={a.id} style={{ ...text.body, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <PortalLink to={KIND_HREFS.action(a.id) ?? "/improvement"} style={{ color: "var(--c-accentText)" }}>{a.displayId != null ? `CA-${a.displayId} ` : ""}{a.title}</PortalLink>
                <StatusPill value={a.status} />
              </div>
            ))}
            {pending.map((t) => (
              <div key={t} style={{ ...text.body, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: theme.color.text }}>{t}</span>
                <span style={text.meta}>not yet raised</span>
              </div>
            ))}
          </div>
          {canEdit && pending.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <RecordActions note="Each improvement becomes its own corrective action, linked to this incident.">
                {pending.map((t) => (
                  <button key={t} type="button" style={secondaryBtn} disabled={busy === "raise"} onClick={() => raise([t])} title={t}>
                    Raise: {t.length > 40 ? `${t.slice(0, 40)}…` : t}
                  </button>
                ))}
                {pending.length > 1 && <button type="button" style={secondaryBtn} disabled={busy === "raise"} onClick={() => raise(pending)}>{busy === "raise" ? "Raising…" : `Raise all ${pending.length}`}</button>}
              </RecordActions>
            </div>
          )}
          {error && !editing && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 6 }}>{error}</div>}
        </div>
      )}
    </section>
  );
}
